// How far the live price may sit from a market figure before the queue calls it out. Shared with
// the listing score's price criterion.
export const PRICE_GAP_THRESHOLD = 0.1;

export type PriceDeltaBasis = "intelligence" | "sold";
export type PriceDeltaTone = "above" | "below" | "within" | "noSold" | "none";

export interface IPriceDelta {
  /** Which market figure the chip compares against; null when there is nothing to compare to. */
  basis: PriceDeltaBasis | null;
  /** Signed gap as a fraction of the basis figure (0.15 = 15% above). Null when there is no basis. */
  fraction: number | null;
  tone: PriceDeltaTone;
  /** The chip's text, e.g. "+229% vs intelligence", "within 10%", "no sold data". */
  label: string;
}

const fractionOf = (live: number, figure: number): number => (live - figure) / figure;

const formatPercent = (fraction: number): string => {
  const percent = Math.round(Math.abs(fraction) * 100);
  return `${fraction >= 0 ? "+" : "−"}${percent}%`;
};

/**
 * The "Price vs market" delta chip. Intelligence (eBay asking prices) is compared first: when the
 * live price is more than 10% away from it the chip says so. Otherwise the yard's own sold average
 * gets the same test. Within 10% of whatever is available reads "within 10%"; with no sold data
 * and no intelligence gap it says "no sold data", and with nothing at all "no data".
 */
export function computePriceDelta(
  livePrice: number | null | undefined,
  intelligenceAverage: number | null | undefined,
  soldAverage: number | null | undefined,
  threshold: number = PRICE_GAP_THRESHOLD
): IPriceDelta {
  const live = livePrice != null && livePrice > 0 ? livePrice : null;
  const intelligence = intelligenceAverage != null && intelligenceAverage > 0 ? intelligenceAverage : null;
  const sold = soldAverage != null && soldAverage > 0 ? soldAverage : null;

  if (live == null || (intelligence == null && sold == null)) {
    return { basis: null, fraction: null, tone: "none", label: "no data" };
  }

  const candidates: { basis: PriceDeltaBasis; fraction: number }[] = [];
  if (intelligence != null) candidates.push({ basis: "intelligence", fraction: fractionOf(live, intelligence) });
  if (sold != null) candidates.push({ basis: "sold", fraction: fractionOf(live, sold) });

  const outside = candidates.find((candidate) => Math.abs(candidate.fraction) > threshold);
  if (outside) {
    return {
      basis: outside.basis,
      fraction: outside.fraction,
      tone: outside.fraction > 0 ? "above" : "below",
      label: `${formatPercent(outside.fraction)} vs ${outside.basis}`,
    };
  }

  if (sold == null) {
    const [only] = candidates;
    return { basis: only.basis, fraction: only.fraction, tone: "noSold", label: "no sold data" };
  }

  const soldCandidate = candidates.find((candidate) => candidate.basis === "sold")!;
  return {
    basis: "sold",
    fraction: soldCandidate.fraction,
    tone: "within",
    label: `within ${Math.round(threshold * 100)}%`,
  };
}

/** Whether the live price is more than the threshold above the given figure. */
export function isPricedAbove(
  livePrice: number | null | undefined,
  figure: number | null | undefined,
  threshold: number = PRICE_GAP_THRESHOLD
): boolean {
  if (livePrice == null || figure == null || figure <= 0 || livePrice <= 0) return false;
  return fractionOf(livePrice, figure) > threshold;
}
